import { Button } from '@berky/ui'
import { RotateCcw } from 'lucide-react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from 'react'

import { encodeScreenshot } from './encode-screenshot'
import type { EncodedScreenshot } from './types'

export interface ScreenshotEditorHandle {
  exportScreenshot: () => Promise<EncodedScreenshot>
}

interface Point {
  x: number
  y: number
}

interface ScreenshotEditorProps {
  screenshot: HTMLCanvasElement
}

/** Screenshot-Vorschau mit genau einem Werkzeug: transparentem gelbem Marker. */
export const ScreenshotEditor = forwardRef<ScreenshotEditorHandle, ScreenshotEditorProps>(
  function ScreenshotEditor({ screenshot }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const activePointer = useRef<number | null>(null)
    const lastPoint = useRef<Point | null>(null)

    const restoreScreenshot = useCallback(() => {
      const canvas = canvasRef.current
      const context = canvas?.getContext('2d')
      if (!canvas || !context) return
      context.globalAlpha = 1
      context.clearRect(0, 0, canvas.width, canvas.height)
      context.drawImage(screenshot, 0, 0)
    }, [screenshot])

    useEffect(() => {
      restoreScreenshot()
    }, [restoreScreenshot])

    useImperativeHandle(
      ref,
      () => ({
        exportScreenshot: async () => {
          const canvas = canvasRef.current
          if (!canvas) throw new Error('Der Screenshot ist noch nicht bereit.')
          return encodeScreenshot(canvas)
        },
      }),
      [],
    )

    function pointFromEvent(event: ReactPointerEvent<HTMLCanvasElement>): Point {
      const canvas = event.currentTarget
      const bounds = canvas.getBoundingClientRect()
      return {
        x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
        y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
      }
    }

    function startMarking(event: ReactPointerEvent<HTMLCanvasElement>) {
      event.currentTarget.setPointerCapture(event.pointerId)
      activePointer.current = event.pointerId
      lastPoint.current = pointFromEvent(event)
    }

    function continueMarking(event: ReactPointerEvent<HTMLCanvasElement>) {
      if (activePointer.current !== event.pointerId || !lastPoint.current) return
      const canvas = event.currentTarget
      const context = canvas.getContext('2d')
      if (!context) return

      const point = pointFromEvent(event)
      context.save()
      context.strokeStyle = getComputedStyle(canvas).color
      context.globalAlpha = 0.45
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.lineWidth = canvas.width * 0.018
      context.beginPath()
      context.moveTo(lastPoint.current.x, lastPoint.current.y)
      context.lineTo(point.x, point.y)
      context.stroke()
      context.restore()
      lastPoint.current = point
    }

    function stopMarking(event: ReactPointerEvent<HTMLCanvasElement>) {
      if (activePointer.current !== event.pointerId) return
      activePointer.current = null
      lastPoint.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    }

    return (
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Markieren Sie die relevante Stelle mit dem gelben Textmarker.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start sm:self-auto"
            onClick={restoreScreenshot}
          >
            <RotateCcw aria-hidden="true" />
            Markierung zurücksetzen
          </Button>
        </div>
        <div className="overflow-auto rounded-md border bg-muted/30 p-1">
          <canvas
            ref={canvasRef}
            width={screenshot.width}
            height={screenshot.height}
            className="block h-auto max-w-full touch-none cursor-crosshair rounded-sm text-marker"
            role="img"
            aria-label="Screenshot des aktuellen Bildschirms. Mit Maus, Stift oder Finger gelb markieren."
            onPointerDown={startMarking}
            onPointerMove={continueMarking}
            onPointerUp={stopMarking}
            onPointerCancel={stopMarking}
          />
        </div>
      </div>
    )
  },
)
